import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  photosApi,
  Photo,
  CreatePhotoDto,
  UpdatePhotoDto,
  GetPhotosDto,
  PhotoStats,
} from '../api/photos';

const DEFAULT_PAGE_SIZE = 24;

interface UsePhotosReturn {
  // Data
  photos: Photo[];
  statistics: PhotoStats | null;
  loading: boolean;
  error: string | null;
  // Pagination
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  setPage: (page: number) => void;
  setFilters: (updates: Partial<GetPhotosDto>) => void;
  filters: GetPhotosDto;

  // Operations
  createPhoto: (formData: FormData) => Promise<Photo | null>;
  updatePhoto: (id: string, data: UpdatePhotoDto) => Promise<Photo | null>;
  replacePhotoImage: (id: string, formData: FormData) => Promise<Photo | null>;
  deletePhoto: (id: string) => Promise<boolean>;
  setAvatar: (photoId: string) => Promise<Photo | null>;
  refreshData: () => Promise<void>;

  // Utility functions
  getPhotoById: (id: string) => Photo | undefined;
  getPhotosByBird: (birdId: string) => Photo[];
  getPhotosBySpecies: (speciesId: string) => Photo[];
  getAvatarPhotos: () => Photo[];
}

export const usePhotos = (initialFilters: GetPhotosDto = {}): UsePhotosReturn => {
  const defaultFilters: GetPhotosDto = {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    ...initialFilters,
  };

  const [filters, setFiltersState] = useState<GetPhotosDto>(defaultFilters);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(defaultFilters.page ?? 1);
  const [limit, setLimitState] = useState(defaultFilters.limit ?? DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [statistics, setStatistics] = useState<PhotoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Load photos data with pagination
  const loadPhotos = useCallback(async (overrideFilters?: GetPhotosDto) => {
    const activeFilters = overrideFilters ?? filtersRef.current;
    const merged = { ...defaultFilters, ...activeFilters };
    try {
      setLoading(true);
      setError(null);

      const result = await photosApi.getPhotosPaginated(merged);
      setPhotos(result.data);
      setTotal(result.total);
      setPageState(result.page);
      setLimitState(result.limit);
      setTotalPages(result.totalPages);
      if (!overrideFilters) {
        setFiltersState(merged);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load photos';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const setPage = useCallback((newPage: number) => {
    const next = { ...filtersRef.current, page: newPage };
    setFiltersState(next);
    loadPhotos(next);
  }, [loadPhotos]);

  const setFilters = useCallback((updates: Partial<GetPhotosDto>) => {
    const next = { ...filtersRef.current, ...updates, page: 1 };
    setFiltersState(next);
    loadPhotos(next);
  }, [loadPhotos]);

  // Load statistics - stable function reference
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await photosApi.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load photo statistics:', err);
    }
  }, []);

  // Create stable function references for operations
  const loadStatisticsRef = useRef(loadStatistics);
  loadStatisticsRef.current = loadStatistics;

  // Create photo
  const uploadPhoto = useCallback(async (formData: FormData) => {
    try {
      const newPhoto = await photosApi.uploadPhoto(formData);
      setPhotos(prev => [newPhoto, ...prev]);
      toast.success('Photo uploaded successfully');
      await loadStatistics();
      return newPhoto;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload photo';
      toast.error(errorMessage);
      throw err;
    }
  }, [loadStatistics]);

  // Update photo
  const updatePhoto = useCallback(async (id: string, data: UpdatePhotoDto) => {
    try {
      const updatedPhoto = await photosApi.updatePhoto(id, data);
      setPhotos(prev => 
        prev.map(photo => 
          photo.id === id ? updatedPhoto : photo
        )
      );
      toast.success('Photo updated successfully');
      await loadStatistics();
      return updatedPhoto;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update photo';
      toast.error(errorMessage);
      throw err;
    }
  }, [loadStatistics]);

  // Replace photo image (after editing)
  const replacePhotoImage = useCallback(async (id: string, formData: FormData) => {
    try {
      const updatedPhoto = await photosApi.replacePhotoImage(id, formData);
      setPhotos(prev => 
        prev.map(photo => 
          photo.id === id ? updatedPhoto : photo
        )
      );
      toast.success('Image saved successfully');
      await loadStatistics();
      return updatedPhoto;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save image';
      toast.error(errorMessage);
      return null;
    }
  }, [loadStatistics]);

  // Delete photo
  const deletePhoto = useCallback(async (id: string) => {
    try {
      await photosApi.deletePhoto(id);
      setPhotos(prev => prev.filter(photo => photo.id !== id));
      toast.success('Photo deleted successfully');
      await loadStatistics();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete photo';
      toast.error(errorMessage);
      return false;
    }
  }, [loadStatistics]);

  // Set avatar
  const setAvatar = useCallback(async (photoId: string): Promise<Photo | null> => {
    try {
      // Find the photo to get its birdId
      const photo = photos.find(p => p.id === photoId);
      if (!photo || !photo.birdId) {
        toast.error('Photo must be associated with a bird to set as avatar');
        return null;
      }
      
      const updatedPhoto = await photosApi.setAsAvatar(photoId, photo.birdId);
      setPhotos(prev =>
        prev.map(p => ({
          ...p,
          isAvatar: p.birdId === photo.birdId ? p.id === photoId : p.isAvatar,
        })),
      );
      toast.success('Avatar set successfully');
      return updatedPhoto;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set avatar';
      toast.error(errorMessage);
      return null;
    }
  }, [photos]);

  const loadPhotosRef = useRef(loadPhotos);
  loadPhotosRef.current = loadPhotos;

  const refreshData = useCallback(async () => {
    await Promise.all([
      loadPhotosRef.current(filtersRef.current),
      loadStatisticsRef.current(),
    ]);
  }, []);

  // Utility functions
  const getPhotoById = useCallback((id: string): Photo | undefined => {
    return photos.find(photo => photo.id === id);
  }, [photos]);

  const getPhotosByBird = useCallback((birdId: string): Photo[] => {
    return photos.filter(photo => photo.birdId === birdId);
  }, [photos]);

  const getPhotosBySpecies = useCallback((speciesId: string): Photo[] => {
    return photos.filter(photo => photo.speciesId === speciesId);
  }, [photos]);

  const getAvatarPhotos = useCallback((): Photo[] => {
    return photos.filter(photo => photo.isAvatar);
  }, [photos]);

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadPhotos(defaultFilters),
        loadStatisticsRef.current(),
      ]);
    };
    loadInitialData();
  }, []);

  return {
    photos,
    statistics,
    loading,
    error,
    total,
    page,
    limit,
    totalPages,
    setPage,
    setFilters,
    filters,
    createPhoto: uploadPhoto,
    updatePhoto,
    replacePhotoImage,
    deletePhoto,
    setAvatar,
    refreshData,
    getPhotoById,
    getPhotosByBird,
    getPhotosBySpecies,
    getAvatarPhotos,
  };
}; 